import { format, parse, isValid, formatDistanceToNow, eachDayOfInterval, differenceInMonths, getHours, getMinutes } from 'date-fns';
import { companyContextService } from '@/services/CompanyContextService';
export function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
export function pluralize(n, string) {
  if (n !== 1) {
    string += 's';
  }
  return string;
}
export function pluralizeWithCount(n, string) {
  return `${n} ${pluralize(n, string)}`;
}

// Entry-specific pluralization helpers
export function pluralizeEntry(count, shouldCapitalize = false) {
  const word = count === 1 ? 'entry' : 'entries';
  return shouldCapitalize ? capitalize(word) : word;
}
export function pluralizeEntryWithCount(count) {
  return `${count} ${pluralizeEntry(count)}`;
}
export function convertNewlinesToBreaks(htmlText) {
  if (!htmlText) return '';
  return htmlText.replace(/\n/g, '<br>');
}
export function getSubscriptionType(product) {
  const entry = Object.entries(product).find(([, value]) => value === true);
  return entry ? entry[0].toUpperCase() : null;
}
export function mapTrackingData(charge, me, service) {
  return {
    company: me.company.name,
    user: {
      name: me.name,
      email: me.email
    },
    userRole: me.role,
    subscriptionType: getSubscriptionType(me.company.products),
    hasQboMapping: !!service,
    amount: charge.amount
  };
}

/**
 * Standard email validation regex pattern
 * This pattern supports:
 * - Single character before @ symbol
 * - Dash characters in the local part
 * - Case insensitive letters
 * - Multiple comma-separated emails (with optional space after comma)
 * Use this for consistent email validation across the application
 */
export const emailRegexPattern = /^([a-zA-Z0-9][a-zA-Z0-9_.+\-!#$%&'*\/=?^`{|}~]*@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(, ?[a-zA-Z0-9][a-zA-Z0-9_.+\-!#$%&'*\/=?^`{|}~]*@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})*$/;

/**
 * Regex pattern for validating a single email address (no comma-separated values)
 * Used specifically for From email addresses in email delivery
 */
export const singleEmailRegexPattern = /^[a-zA-Z0-9][a-zA-Z0-9_.+\-!#$%&'*\/=?^`{|}~]*@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

/**
 * Validates an email address or multiple comma-separated email addresses
 * @param {string} email - The email(s) to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidEmail(email) {
  if (!email) return false;
  return emailRegexPattern.test(email);
}

/**
 * Validates a single email address (no comma-separated values)
 * Use this for validating From email addresses in email delivery
 * @param {string} email - The email to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidSingleEmail(email) {
  if (!email) return false;
  return singleEmailRegexPattern.test(email);
}
export function convertRates(rates) {
  if (!rates) {
    return [];
  }
  let result = [];
  if (rates.standard) {
    result.push({
      label: 'Standard',
      amount: rates.standard
    });
  }
  if (rates.other) {
    const others = rates.other.map(rate => ({
      ...rate,
      value: rate.label
    }));
    result = [...result, ...others];
  }
  return result;
}

/**
 * Parse a date string to a local Date (or null if absent/invalid).
 * YYYY-MM-DD is parsed as local midnight to avoid the UTC-offset day shift that
 * new Date("YYYY-MM-DD") causes in US timezones; other inputs fall back to new Date().
 * Use for feeding date pickers (LlDatePicker expects a Date, not a string).
 */
export function parseLocalDate(value) {
  if (!value) return null;
  const date = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? parse(value, 'yyyy-MM-dd', new Date()) : new Date(value);
  return isValid(date) ? date : null;
}
export function getShortDateString(datesrting) {
  if (datesrting === undefined) {
    return '';
  }
  // date-fns parse() treats YYYY-MM-DD as local time, avoiding the UTC midnight
  // shift that new Date("YYYY-MM-DD") causes in US timezones.
  const date = typeof datesrting === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(datesrting) ? parse(datesrting, 'yyyy-MM-dd', new Date()) : new Date(datesrting);
  if (!isValid(date)) return '';
  return format(date, 'MM/dd/yy');
}
export function formatLongDateString(datesrting) {
  if (datesrting === undefined || datesrting === null) {
    return '';
  }

  // Keep YYYY-MM-DD parsing in local time to avoid timezone date shifts.
  const date = typeof datesrting === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(datesrting) ? parse(datesrting, 'yyyy-MM-dd', new Date()) : new Date(datesrting);
  if (!isValid(date)) return '';
  return format(date, 'MMMM d, yyyy');
}
export function getShortDateTimeString(datesrting) {
  if (datesrting === undefined) {
    return '';
  }
  return `${new Date(datesrting).toShortDateString()} ${new Date(datesrting).toLocaleTimeString()}`;
}
export function getConfig(config) {
  const list = Object.keys(window.leanlaw.features).filter(feature => window.leanlaw.features[feature]);
  const localFeatures = list.length > 0 ? list.join(',') : {};
  config.baseURL = `${window.leanlaw.domain}/v1`;
  config.withCredentials = true;
  config.headers['x-leanlaw-source'] = window.leanlaw.source;
  config.headers['x-leanlaw-version'] = 'v1'; // todo inject version info
  config.headers['x-leanlaw-accountId'] = window.leanlaw.currentAccountId;
  config.headers['x-leanlaw-features'] = localFeatures;

  // Add company context header for mismatch detection
  // Use the page-specific company ID (me.company.id) captured at page load,
  // NOT window.leanlaw.currentAccountId which holds user.id, not the company ID.
  const pageCompanyId = companyContextService.getPageLoadCompanyId();
  if (pageCompanyId) {
    config.headers['x-leanlaw-companyid'] = pageCompanyId;
  }
  if (window.leanlaw.token) {
    config.headers.Authorization = `Bearer ${window.leanlaw.token}`;
  }
  return config;
}
export function round2(n) {
  // Epsilon corrects IEEE 754 midpoint errors (e.g. 1.005 * 100 evaluates
  // to 100.4999... instead of 100.5). At 1e-8 magnitude this nudge is too
  // small to cause a false round-up on real financial values.
  const scaled = n * 100;
  return Math.round(scaled + Math.sign(scaled || 1) * 1e-8) / 100;
}
export function calculateEntryAmount(rate, hours) {
  return round2((rate || 0) * (hours || 0));
}
export function convertToDate(time) {
  if (!time) return;
  // Normalize to HH:mm:ss format
  const timeStr = time.length === 5 ? `${time}:00` : time;
  const date = parse(timeStr, 'HH:mm:ss', new Date());
  return isValid(date) ? date : undefined;
}
export function timeAgo(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (!isValid(date)) return '';
  return formatDistanceToNow(date, {
    addSuffix: true
  });
}
export function convertUTCtoLocal(utcDateStr) {
  if (!utcDateStr) return '';
  const date = new Date(utcDateStr);
  if (!isValid(date)) return '';
  // Format: "15 January 2024, 2:30 pm"
  return format(date, 'd MMMM yyyy, h:mm a').toLowerCase().replace(/(\d+ \w+ \d+, \d+:\d+ )/, m => m.charAt(0).toUpperCase() + m.slice(1));
}
export function clearModalHack() {
  // angular-bootstrap is somehow adding a modal backdrop
  // This is a hack to fix that, because I've checked the source and
  // tried to step through the code but couldn't make any progress
  //
  setTimeout(() => {
    $('.modal-backdrop.in').remove();
    // This won't flash because the backdrop is hidden
    // This simply removes the elements so they don't stack up once
    // the body doesn't have the "show-modal-overlay" class
  }, 50);
}
export function getUserRoleString(user) {
  return {
    principal: 'Principal',
    lead: 'Attorney',
    paralegal: 'Paralegal',
    operator: 'Operator',
    accountant: 'Accountant',
    timekeeper: 'Timekeeper'
  }[user.role];
}
export const USER_ROLES = {
  PRINCIPAL: 'principal',
  LEAD: 'lead',
  PARALEGAL: 'paralegal',
  OPERATOR: 'operator',
  ACCOUNTANT: 'accountant',
  TIMEKEEPER: 'timekeeper'
};
export const roleHelpers = {
  // Check if role is an operator or accountant
  isOperatorOrAccountantRole(role) {
    return role === USER_ROLES.OPERATOR || role === USER_ROLES.ACCOUNTANT;
  }
};

/**
 * Filters users to get those assignable to matters/tasks, excluding specified roles
 * @param {Array} users - Array of user objects with role property
 * @param {Object} options - Filtering options
 * @param {Array} options.excludeRoles - Array of roles to exclude (defaults to ['timekeeper', 'accountant'])
 * @param {boolean} options.sort - Whether to sort by name (defaults to true)
 * @returns {Array} Filtered and optionally sorted array of assignable users
 */
export function getAssignableUsers(users, options = {}) {
  if (!users || !Array.isArray(users)) {
    return [];
  }
  const {
    excludeRoles = [USER_ROLES.TIMEKEEPER, USER_ROLES.ACCOUNTANT],
    sort = true
  } = options;
  const filteredUsers = users.filter(user => !excludeRoles.includes(user.role));
  if (sort) {
    return filteredUsers.sort((a, b) => a.name.localeCompare(b.name));
  }
  return filteredUsers;
}

/**
 * Format a value or return a default placeholder for null/undefined values
 * @param {*} value - The value to format
 * @param {string} defaultValue - The default value to return if value is null/undefined (default: '—')
 * @returns {string} - The formatted value or default placeholder
 * @example
 * formatOrDefault(null) // returns '—'
 * formatOrDefault('', '-') // returns '-'
 * formatOrDefault('John Doe') // returns 'John Doe'
 * formatOrDefault(0) // returns 0 (treats 0 as valid)
 */
export function formatOrDefault(value, defaultValue = '') {
  // Treat 0 and false as valid values, only check for null/undefined/empty string
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  return value;
}
export function formatCurrency(amount) {
  const numberFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  });
  if (!amount) {
    return '$0.00';
  }
  if (amount < 0) {
    return `(${numberFormat.format(Math.abs(amount))})`;
  }
  return numberFormat.format(amount);
}

// ─── Currency input utilities ────────────────────────────────────
// Three small helpers for text inputs that accept dollar amounts.
// No external package needed — Intl.NumberFormat covers everything.

/**
 * Strips commas and parses the string to a number.
 * Returns undefined for empty / non-numeric input.
 *
 * @param {string} str  e.g. "1,234.56" or "1234.56"
 * @returns {number | undefined}
 */
export function parseCurrencyInput(str) {
  if (str == null || str === '') return undefined;
  const raw = String(str).replace(/,/g, '').trim();
  const parsed = parseFloat(raw);
  return isNaN(parsed) ? undefined : parsed;
}

/**
 * Formats a number as a display-only currency string (commas + 2dp, no $ sign).
 * Use in blur / read-only contexts.
 *
 * @param {number | null | undefined} amount  e.g. 1234567.89
 * @returns {string}  e.g. "1,234,567.89"
 */
export function formatCurrencyInputDisplay(amount) {
  if (amount == null || amount === '' || isNaN(Number(amount)) || Number(amount) <= 0) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(amount));
}

/**
 * Formats an in-progress currency string in real-time as the user types.
 * Adds commas to the integer part while keeping the decimal suffix exactly
 * as typed (so "1234." stays "1,234." mid-entry).
 * Also computes the correct cursor position after formatting so the caret
 * doesn't jump when commas are inserted/removed.
 *
 * @param {string} inputValue  current raw value of the <input> element
 * @param {number} cursorPos   selectionEnd before formatting
 * @returns {{ formatted: string, newCursor: number }}
 *
 * @example
 * formatCurrencyInputTyping('1234', 4)  // { formatted: '1,234', newCursor: 5 }
 * formatCurrencyInputTyping('1234.5', 6) // { formatted: '1,234.5', newCursor: 7 }
 */
export function formatCurrencyInputTyping(inputValue, cursorPos) {
  // Strip everything except digits and one decimal point
  let raw = String(inputValue).replace(/[^0-9.]/g, '');
  const dotIdx = raw.indexOf('.');
  if (dotIdx !== -1) {
    raw = raw.slice(0, dotIdx + 1) + raw.slice(dotIdx + 1).replace(/\./g, '');
  }

  // Format integer part with commas; preserve decimal suffix exactly as typed
  const [intStr, decStr] = raw.split('.');
  const formattedInt = intStr ? Number(intStr).toLocaleString('en-US') : '';
  const formatted = decStr !== undefined ? formattedInt + '.' + decStr : formattedInt;

  // Restore cursor: count significant (non-comma) chars before old cursor position,
  // then walk the formatted string until the same count is reached.
  const sigBeforeCursor = String(inputValue).slice(0, cursorPos).replace(/,/g, '').length;
  let newCursor = formatted.length;
  let sig = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (sig === sigBeforeCursor) {
      newCursor = i;
      break;
    }
    if (formatted[i] !== ',') sig++;
  }
  return {
    formatted,
    newCursor
  };
}
// ────────────────────────────────────────────────────────────────────────────

export function convertToFormattedCurrency(amount, ifUndefinedString) {
  const numberFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  });
  if (!amount && ifUndefinedString) {
    return undefined;
  }

  // Check if amount is not a valid number
  if (isNaN(amount) || amount === null || amount === undefined) {
    return String(amount); // Return the input as a string
  }
  if (!amount) {
    return '$0.00';
  }
  if (amount < 0) {
    return `(${numberFormat.format(Math.abs(amount))})`;
  }
  return numberFormat.format(amount);
}
export function getSortedArray(items) {
  if (!items || !Array.isArray(items)) {
    return [];
  }
  return items.sort((a, b) => a.name.localeCompare(b.name));
}
export const updateOrder = fields => fields.map((field, index) => ({
  ...field,
  order: index + 1
}));
export function isNotEmpty(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}
export function formatTime(isoTime) {
  if (!isoTime) return '-';
  let date;

  // Check if it's a time-only string (HH:mm:ss or HH:mm format)
  if (typeof isoTime === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(isoTime)) {
    // Normalize to HH:mm:ss format
    const timeStr = isoTime.length === 5 ? `${isoTime}:00` : isoTime;
    date = parse(timeStr, 'HH:mm:ss', new Date());
  } else {
    // Parse as full ISO timestamp
    date = new Date(isoTime);
  }
  if (!isValid(date)) return '-';
  return format(date, 'h:mm a');
}

/**
 * Generate an array of all dates between start and end (inclusive)
 * @param {Date} startDate - The start date
 * @param {Date} endDate - The end date
 * @returns {Date[]} Array of dates from start to end
 */
export function getDatesInRange(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (!isValid(start) || !isValid(end)) return [];
  return eachDayOfInterval({
    start,
    end
  });
}

/**
 * Calculate months difference between two dates
 * @param {Date} startDate - The start date
 * @param {Date} endDate - The end date
 * @returns {number} Number of months difference
 */
export function getMonthsDifference(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  return differenceInMonths(endDate, startDate);
}

/**
 * Formats a Date object or date string to HH:MM string format
 * @param {Date|string} date - The date object or date string to format
 * @returns {string|null} - Time in HH:MM format or null if invalid date
 */
export const convertToHHSS = date => {
  if (!date) return null;

  // If it's already an HH:mm string, return it
  if (typeof date === 'string' && /^\d{2}:\d{2}/.test(date)) {
    return date.slice(0, 5);
  }

  // Convert to Date if it's a string (e.g., ISO date string)
  const dateObj = date instanceof Date ? date : new Date(date);

  // Check if valid date using date-fns
  if (!isValid(dateObj)) return null;
  return format(dateObj, 'HH:mm');
};

/**
 * Custom modulo function that handles floating point precision issues
 * This is necessary to ensure that e.g. mod(0.5,0.1) returns 0.
 * Built-in operator results in rounding errors, e.g. 0.5 % 0.1 = 0.99999999999..
 * @param {number} a - The dividend
 * @param {number} b - The divisor
 * @returns {number} The remainder after division, handling floating point precision
 */
export function mod(a, b) {
  return Math.round(a * 100) % Math.round(b * 100);
}

// Constants for allocation and currency operations
export const CURRENCY_TOLERANCE = 0.01;
export const PERCENTAGE_SCALE = 100;
export const DEFAULT_STEP = 0.01;

/**
 * Creates a standardized allocation object for revenue allocation
 * @param {Object} user - The user account object
 * @param {number} amount - The allocation amount
 * @param {number} chargeAmount - The total charge amount for percentage calculation
 * @param {boolean} isMain - Whether this is the main invoice user
 * @returns {Object} Formatted allocation object
 */
export function createAllocation(user, amount = 0, chargeAmount = 0, isMain = false) {
  const percentage = chargeAmount > 0 ? amount / chargeAmount * PERCENTAGE_SCALE : 0;
  return {
    account: user,
    amount: Number(amount) || 0,
    percentage: round2(percentage),
    main: isMain,
    id: user.id
  };
}

/**
 * Converts raw allocation data to formatted allocation objects
 * @param {Array} rawAllocations - Raw allocation data from API
 * @param {Array} users - Available users list
 * @param {number} chargeAmount - Total charge amount
 * @param {string} mainAccountId - ID of the main invoice account
 * @returns {Array} Formatted allocation objects
 */
export function mapRawAllocationsToFormData(rawAllocations, users, chargeAmount, mainAccountId) {
  return rawAllocations.map(allocation => {
    const user = users.find(u => u.id === allocation.id);
    if (!user) return null;
    return createAllocation(user, allocation.amount, chargeAmount, mainAccountId === allocation.id);
  }).filter(Boolean); // Remove null entries
}

/**
 * Converts allocation form data to API format
 * @param {Array} allocations - Form allocation data
 * @returns {Object} API-formatted allocation data
 */
export function mapAllocationsToApiFormat(allocations) {
  if (!Array.isArray(allocations)) {
    return {};
  }
  const accounts = allocations.map(a => ({
    id: a.account.id,
    amount: Number(a.amount) || 0
  }));
  return accounts.length > 0 ? {
    accounts
  } : {};
}

/**
 * Checks if two monetary amounts are approximately equal within a tolerance of 0.01 (1 cent)
 * This is useful for comparing currency values where small floating-point differences may occur
 * @param {number} amount1 - First amount to compare
 * @param {number} amount2 - Second amount to compare
 * @param {number} tolerance - Optional tolerance (defaults to 0.01)
 * @returns {boolean} True if the amounts are within the tolerance, false otherwise
 */
export function isAmountEqual(amount1, amount2, tolerance = 0.01) {
  return Math.abs((amount1 || 0) - (amount2 || 0)) <= tolerance;
}

/**
 * Formats a Date object to YYYY-MM-DD string format
 * This function mimics the behavior of the legacy yyyymmdddash() Date prototype extension
 * @param {Date} date - The date to format
 * @returns {string} The formatted date string in YYYY-MM-DD format
 */
export function yyyymmdddash(date) {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyy-MM-dd');
}

// Re-export datetime utilities from dateTimeUtils.js for convenience
// Story #41530: Centralized localDateTime display formatting
export { formatTimeFrom24h, calculateEndTimeFrom24h, formatTimeFromDate, getEntryStartTime, getEntryEndTime, createDateFromLocalTime, calculateEndDateFromHours, getEntryTimeDates } from '../utils/dateTimeUtils';